import { useEffect } from 'react';

/**
 * `data-para` を持つ要素を、親要素の画面内の進み具合に応じて上下にずらす。
 * 値が正なら遅れて動き、負なら先に動く。
 *
 * transform だけを触り、rAF で間引くのでスクロール性能に影響しない。
 */
export default function useParallax(rootRef, enabled = true) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const targets = Array.from(root.querySelectorAll('[data-para]'));
    if (targets.length === 0) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!enabled || reduce) {
      targets.forEach((el) => { el.style.transform = ''; });
      return undefined;
    }

    let ticking = false;

    const apply = () => {
      ticking = false;
      const vh = window.innerHeight;
      targets.forEach((el) => {
        const parent = el.parentElement;
        if (!parent) return;
        const r = parent.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        const progress = (r.top + r.height / 2 - vh / 2) / vh;
        const speed = parseFloat(el.dataset.para);
        el.style.transform = `translate3d(0, ${(progress * speed * 100).toFixed(2)}px, 0)`;
      });
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      targets.forEach((el) => { el.style.transform = ''; });
    };
  }, [rootRef, enabled]);
}
